import { withAdminSoloTagsQuery } from "../../../lib/adminSoloTags";

/** 定制工作台移动端视口上限（与手机工作台验收宽度对齐）。 */
export const CUSTOMIZATION_MOBILE_MAX_WIDTH_PX = 768;

/** 商机入口进入定制时附带的 `from` 查询值，供手机顶栏返回商机工作台。 */
export const CUSTOMIZATION_ENTRY_FROM_OPPORTUNITY = "opportunity";

/** 商机工作台用于恢复「关联定制」抽屉的查询键。 */
export const OPPORTUNITY_OPEN_CUSTOMIZATION_QUERY_KEY = "openCustomization";

/** 商机工作台用于恢复「增定制」抽屉的查询键。 */
export const OPPORTUNITY_OPEN_ADD_CUSTOMIZATION_QUERY_KEY = "openAddCustomization";

/** 手机定制来源：关联定制列表，或增定制创建/复制。 */
export type OpportunityMobileCustomizationEntry = "linked" | "add";

/** 在既有 search 上写入来源标记，保留 JiDiaoNo 等业务参数。 */
export function withCustomizationEntryFrom(
  search: string | undefined,
  from: string,
): string {
  const params = new URLSearchParams(
    search?.startsWith("?") ? search.slice(1) : search ?? "",
  );
  params.set("from", from);
  const query = params.toString();
  return query ? `?${query}` : "";
}

/**
 * 构造从商机进入手机定制的查询串。
 * `entry=add` 表示增定制创建/复制；缺省为关联定制。顶栏返回据此打开对应抽屉。
 */
export function buildOpportunityMobileCustomizationEntrySearch(options: {
  opportunityId: number;
  returnTo: string;
  jiDiaoNo?: string;
  entry?: OpportunityMobileCustomizationEntry;
}): string {
  const params = new URLSearchParams();
  const jiDiaoNo = options.jiDiaoNo?.trim();
  if (jiDiaoNo) {
    params.set("JiDiaoNo", jiDiaoNo);
  }
  params.set("from", CUSTOMIZATION_ENTRY_FROM_OPPORTUNITY);
  params.set("opportunityId", String(options.opportunityId));
  if (options.entry === "add") {
    params.set("entry", "add");
  }
  params.set("returnTo", options.returnTo);
  return `?${params.toString()}`;
}

/**
 * 解析手机定制顶栏应返回的商机地址。
 * 关联定制入口附带 `openCustomization`；增定制入口附带 `openAddCustomization`。
 * returnTo 仅允许商机工作台相对路径，防止开放重定向。
 */
export function buildOpportunityAssociatedCustomizationReturnPath(
  searchParams: Pick<URLSearchParams, "get">,
): string | null {
  if (searchParams.get("from") !== CUSTOMIZATION_ENTRY_FROM_OPPORTUNITY) {
    return null;
  }
  const opportunityId = Number(searchParams.get("opportunityId"));
  if (!Number.isFinite(opportunityId) || opportunityId <= 0) {
    return sanitizeOpportunityWorkbenchReturnTo(searchParams.get("returnTo"))
      ?? "/crm/opportunities/workbench";
  }
  const base =
    sanitizeOpportunityWorkbenchReturnTo(searchParams.get("returnTo"))
    ?? "/crm/opportunities/workbench";
  const pathname = base.includes("?") ? base.slice(0, base.indexOf("?")) : base;
  const existingSearch = base.includes("?") ? base.slice(base.indexOf("?") + 1) : "";
  const params = new URLSearchParams(existingSearch);
  const restoreKey =
    searchParams.get("entry") === "add"
      ? OPPORTUNITY_OPEN_ADD_CUSTOMIZATION_QUERY_KEY
      : OPPORTUNITY_OPEN_CUSTOMIZATION_QUERY_KEY;
  params.set(restoreKey, String(opportunityId));
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

/** 只允许回到商机工作台相对路径。 */
export function sanitizeOpportunityWorkbenchReturnTo(raw: string | null | undefined): string | null {
  const value = raw?.trim();
  if (!value) {
    return null;
  }
  if (!value.startsWith("/crm/opportunities/workbench")) {
    return null;
  }
  if (value.startsWith("//") || value.includes("://") || /[\s<>]/.test(value)) {
    return null;
  }
  return value;
}

/** 判断当前是否应按手机定制工作台打开。 */
export function isCustomizationMobileDevice(
  media: Pick<MediaQueryList, "matches"> | null | undefined = readMobileMediaQuery(),
): boolean {
  return Boolean(media?.matches);
}

/**
 * 构造进入定制工作台的业务地址。
 * 已有项目在手机端走 customization-mobile；空白新建仍共用 PC `/new/workbench`。
 */
export function buildCustomizationWorkbenchEntryPath(
  projectId: string,
  options: { search?: string; forceMobile?: boolean } = {},
): string {
  const encodedId = encodeURIComponent(projectId);
  const search = normalizeSearch(options.search);
  const useMobile =
    projectId !== "new"
    && (options.forceMobile ?? isCustomizationMobileDevice());
  const base = useMobile
    ? `/crm/customization-mobile/projects/${encodedId}/workbench`
    : `/crm/customization/projects/${encodedId}/workbench`;
  return `${base}${search}`;
}

/** 构造从商机上下文打开关联定制的工作台地址。 */
export function buildOpportunityCustomizationWorkbenchEntryPath(
  opportunityId: number,
  projectId: string,
): string {
  return `/crm/opportunities/${opportunityId}/customization-projects/${encodeURIComponent(projectId)}/workbench`;
}

/**
 * 在新浏览器窗口打开定制工作台。
 * 供定制列表、商机关联定制等入口使用，避免挤掉原列表/商机页签。
 * 新窗口地址附带 TagsView solo 标记，落地后只保留首页与当前工作台页签。
 * @param pendingWindow 可在用户点击同步阶段预开的空白窗，避免 await 后被弹窗拦截。
 * @returns 是否成功唤起新窗口；被拦截时返回 false，调用方应回退到同页跳转。
 *
 * 注意：features 不能带 `noopener`——多数浏览器此时固定返回 null，
 * 会把「已打开新窗」误判成拦截，随后又 navigate 原页。改用打开后清空 opener。
 */
export function openCustomizationWorkbenchInNewWindow(
  path: string,
  pendingWindow?: Window | null,
): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const url = toAbsoluteAppUrl(withAdminSoloTagsQuery(path));
  if (pendingWindow && !pendingWindow.closed) {
    pendingWindow.location.href = url;
    try {
      pendingWindow.opener = null;
    } catch {
      // 跨域或浏览器限制时忽略，新窗已打开。
    }
    return true;
  }
  const opened = window.open(url, "_blank");
  if (!opened) {
    return false;
  }
  try {
    opened.opener = null;
  } catch {
    // 跨域或浏览器限制时忽略，新窗已打开。
  }
  return true;
}

/** 增定制创建过程预开窗的同源路径。 */
export const CUSTOMIZATION_WORKBENCH_OPENING_PATH = "/crm/customization/projects/opening";

/**
 * 在用户点击同步阶段预开「正在创建」过渡页，供后续接口完成后再写入工作台地址。
 * 使用同源业务路径，避免 about:blank 被误认为报错；失败时返回 null。
 */
export function openPendingCustomizationWorkbenchWindow(): Window | null {
  if (typeof window === "undefined") {
    return null;
  }
  return window.open(
    toAbsoluteAppUrl(withAdminSoloTagsQuery(CUSTOMIZATION_WORKBENCH_OPENING_PATH)),
    "_blank",
  );
}

/** @deprecated 请改用 openPendingCustomizationWorkbenchWindow */
export function openBlankCustomizationWorkbenchWindow(): Window | null {
  return openPendingCustomizationWorkbenchWindow();
}

/** 同页回退进入工作台时使用的地址（含 solo 页签标记）。 */
export function buildCustomizationWorkbenchFallbackPath(path: string): string {
  return withAdminSoloTagsQuery(path);
}

/** 读取与手机工作台断点一致的媒体查询结果。 */
function readMobileMediaQuery(): Pick<MediaQueryList, "matches"> | null {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return null;
  }
  return window.matchMedia(`(max-width: ${CUSTOMIZATION_MOBILE_MAX_WIDTH_PX}px)`);
}

/** 规范化工作台跳转时附带的查询字符串。 */
function normalizeSearch(search: string | undefined): string {
  if (!search) return "";
  return search.startsWith("?") ? search : `?${search}`;
}

/** 把应用内相对路径转成可跨窗口打开的绝对地址。 */
function toAbsoluteAppUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${window.location.origin}${normalized}`;
}
